import React, { useEffect, useState } from 'react'
import { useExercise } from '../ExerciseProvider'
import { useTranslation } from '~/hooks/useTranslation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/lib/ui/select'
import { Button } from '~/lib/ui/button'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'
import AylaResponse from '~/components/shared/AylaResponse/index'
import { Label } from '~/lib/ui/label'
import AylaLoading from '~/components/shared/AylaLoading'
import AylaTimeoutError, { aylaPost } from '~/components/shared/AylaTimeout'

const LeisureActivitiesPageTwo = ({ categories, ...sectionProps }) => {
  const { t } = useTranslation('leisure_activities')
  const [numAylaSuggestions, setNumAylaSuggestions] = useState(1)
  const aylaArr = Array.from({ length: numAylaSuggestions }, (_, i) => i + 1)
  const [aylaResponses, setAylaResponses] = useState({})

  const handleAddSuggestionClick = () => {
    setNumAylaSuggestions(numAylaSuggestions + 1)
  }

  return (
    <div className="flex flex-col items-start gap-8">
      {categories.map((category, idx) => (
        <LeasureActivityCategory
          key={`leisure-cat-${idx}`}
          category={category}
          {...sectionProps}
        />
      ))}
      <ActionItem />
      <div>{t('pages.1.instructions')}</div>
      {aylaArr.map((_, idx) => (
        <AylaCategory
          key={`ayla-${idx}`}
          handleAddSuggestionClick={handleAddSuggestionClick}
          aylaResponses={aylaResponses}
          setAylaResponses={setAylaResponses}
        />
      ))}
    </div>
  )
}

export default LeisureActivitiesPageTwo

const LeasureActivityCategory = ({ category }) => {
  const { getResponse } = useExercise()
  const { name, slug } = category
  const userGeneratedActivities = getResponse(0, slug) || []
  const aylaSuggestions = getResponse(1, slug) || []

  const anyActivities =
    userGeneratedActivities.length > 0 || aylaSuggestions.length > 0

  return (
    anyActivities && (
      <div>
        <div className="subtitle">{name}</div>
        <ul>
          {userGeneratedActivities.map((activity, idx) => (
            <li key={`user-activity-${idx}`}>{activity}</li>
          ))}
          {aylaSuggestions.map((activity, idx) => (
            <li key={`ayla-activity-${idx}`}>{activity}</li>
          ))}
        </ul>
      </div>
    )
  )
}

const AylaCategory = ({
  handleAddSuggestionClick,
  aylaResponses,
  setAylaResponses,
  ...props
}) => {
  const { documentId, getResponse, handleChange } = useExercise()
  const { t } = useTranslation('leisure_activities')
  const pageTwoTranslations = t('pages.1')
  const categories = t('categories', { returnObjects: true })
  const [typingComplete, setTypingComplete] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [aylaResponse, setAylaResponse] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(null)
  const { category_dropdown_label, cta } = pageTwoTranslations

  const handleSaveAylaResponse = (e) => {
    const response = aylaResponse.split('\n')
    handleChange(1, selectedCategory.slug, e, response)
  }

  const handleSelectValueChange = (val) => {
    setSelectedCategory(val)
  }

  const userGeneratedActivities = getResponse(0, selectedCategory.slug) || []
  const activityList = userGeneratedActivities.join(', ')
  const previousSuggestions = aylaResponses[selectedCategory.slug] || []
  const promptVariables = { category: selectedCategory.name, activityList }
  const prompt =
    previousSuggestions.length > 0
      ? t('pages.1.ayla_prompt_prev_responses', {
          ...promptVariables,
          previousSuggestions: previousSuggestions,
        })
      : t('pages.1.ayla_prompt', { ...promptVariables })

  useEffect(() => {
    if (typingComplete && aylaResponse) {
      setAylaResponses((prevResponses) => ({
        ...prevResponses,
        [selectedCategory.slug]: [
          prevResponses[selectedCategory.slug] || [],
          aylaResponse,
        ],
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typingComplete])

  const requestAylaStatement = async () => {
    const url = `/job-search/create_ai_response?document_id=${documentId}`
    const data = { ayla_prompt: { prompt } }
    try {
      const res = await aylaPost(url, data)
      setAylaResponse(res.data.ayla_response)
      setSubmitted(false)
      setLoadingStatus('complete')
    } catch {
      setLoadingStatus('failed')
    }
  }
  const handleCtaClick = () => {
    setSubmitted(true)
    setLoadingStatus('loading')
    requestAylaStatement()
  }

  const disabled = selectedCategory === '' || loadingStatus !== null

  return (
    <div className="mb-4 w-full">
      <div className="flex gap-8">
        <Select
          value={selectedCategory}
          onValueChange={handleSelectValueChange}
          className="mb-4 rounded-[20px] border border-[#D3D3D3] bg-[#F7FAFC] p-3 font-roboto text-base"
        >
          <SelectTrigger>
            <SelectValue placeholder={category_dropdown_label} />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category, idx) => (
              <SelectItem key={`category-${idx}`} value={category}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button disabled={disabled} onClick={handleCtaClick}>
          {cta}
          <SavedStatusIcon loadingStatus={loadingStatus} />
        </Button>
      </div>
      {loadingStatus === 'loading' && (
        <div className="flex items-center justify-center">
          <AylaLoading />
        </div>
      )}
      {loadingStatus === 'failed' && (
        <AylaTimeoutError onRetry={handleCtaClick} />
      )}
      {loadingStatus === 'complete' && (
        <AylaResponse
          aylaResponse={aylaResponse}
          submitted={submitted}
          setSubmitted={setSubmitted}
          setTypingComplete={setTypingComplete}
          {...props}
        />
      )}
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        {typingComplete && (
          <Button
            className="mt-4"
            variant="outline"
            onClick={handleSaveAylaResponse}
          >
            {t('pages.1.save')}
          </Button>
        )}
        {typingComplete && (
          <Button className="mt-4" onClick={handleAddSuggestionClick}>
            {t('pages.1.get_another_suggestion')}
          </Button>
        )}
      </div>
    </div>
  )
}

const ActionItem = () => {
  const { t } = useTranslation('leisure_activities')
  const { action_item, text_box_label } = t('pages.1')

  const { getResponse, handleChange } = useExercise()

  const handleTextChange = (e) => {
    handleChange(1, 'action', e)
  }

  const textResponse = getResponse(1, 'action') || ''

  return (
    <div>
      <div>{action_item}</div>
      <div>
        <Label
          htmlFor="leisure-action-idea"
          className="mb-2 block font-roboto text-base font-bold"
        >
          {text_box_label}
        </Label>
        <textarea
          id="vleisure-action-idea"
          className="min-h-[125px] w-full rounded-[20px] border border-[#D3D3D3] bg-[#F7FAFC] p-3 font-roboto text-base"
          onChange={handleTextChange}
          value={textResponse}
        />
      </div>
    </div>
  )
}
